import {  useState } from "react";
import { defineMessages, useIntl } from "react-intl";
import { LocaleLink, useLocaleContext, useLocaleFormatter } from "@ct-react/locale";
import { ArticleType } from "@shared/url";
import { Wrapper } from "../components/layout/wrapper";
import { SearchBar } from "../bundles/client-ui";
import { classNames } from "@ct-react/core";
import { gql } from "@apollo/client";
import RatioImage from "../components/medias/ratio-image";
import ArticleCarousel from "../components/deferred-article-carousel";
import Button from "../components/common/button";
import SpriteIcon from "../components/medias/sprite-icon";
import PostCard from "../components/post-card";
import SeoMetas from "../components/layout/seo-metas";
import { Head } from "../components/layout/head";
import MainImage640 from "../assets/images/agence-immobiliere-inter-agence-nendaz-640w.jpg";
import MainImage1280 from "../assets/images/agence-immobiliere-inter-agence-nendaz-1280w.jpg";
import MainImage1920 from "../assets/images/agence-immobiliere-inter-agence-nendaz-1920w.jpg";
import MainImage2500 from "../assets/images/agence-immobiliere-inter-agence-nendaz-2500w.jpg";
import AboutImageDesktop from "../assets/images//batiment-inter-agence-2560w.jpg";
import AboutImageMobile from "../assets/images//batiment-inter-agence-1200w.jpg";
import { NewsEntry, useNewsContext } from "../contexts/news";

import "./home.scss";

const heroImageSrcSet = [
  `${MainImage640} 640w`,
  `${MainImage1280} 1280w`,
  `${MainImage1920} 1920w`,
  `${MainImage2500} 2500w`,
].join(", ");

const transDefs = defineMessages({
  meta_title: { id: "homepage.meta-title", defaultMessage: "Agence immobilière à Nendaz | Inter-Agence" },
  meta_description: { id: "homepage.meta-description", defaultMessage: "Inter-Agence vous accompagne à Nendaz pour acheter, vendre ou louer. Une équipe locale à votre écoute, au cœur des Alpes valaisannes." },
  hero_subtitle: { id: "homepage.hero.subtitle", defaultMessage: "Au cœur de Nendaz, une agence valaisanne proche des gens et ancrée dans les Alpes." },
  properties_title: { id: "homepage.properties.title", defaultMessage: "À découvrir en ce moment à Nendaz" },
  properties_text: { id: "homepage.properties.text", defaultMessage: "Une sélection de biens à découvrir actuellement. Chalets, appartements ou résidences de vacances, trouvez peut-être ici votre prochain coup de cœur." },
  services_title: { id: "homepage.services.title", defaultMessage: "Votre bien entre de bonnes mains" },
  services_text: { id: "homepage.services.text", defaultMessage: "À Nendaz, l’immobilier est avant tout une question de confiance. Attachés à la région et à ses chalets, nous accompagnons chaque projet avec proximité, bon sens et transparence." },
  services_service_1_title: { id: "homepage.services.service-1.title", defaultMessage: "Gérance" },
  services_service_1_text: { id: "homepage.services.service-1.text", defaultMessage: "Inter-Agence gère votre bien avec soin et professionnalisme pour vous garantir tranquillité et confiance toute l’année." },
  services_service_2_title: { id: "homepage.services.service-2.title", defaultMessage: "Estimation immobilière" },
  services_service_2_text: { id: "homepage.services.service-2.text", defaultMessage: "Nous estimons votre bien avec fiabilité pour vous aider à décider en toute sérénité." },
  services_service_3_title: { id: "homepage.services.service-3.title", defaultMessage: "Maintenance" },
  services_service_3_text: { id: "homepage.services.service-3.text", defaultMessage: "Nous assurons l’entretien et la surveillance de votre bien avec soin, pour votre tranquillité toute l’année." },
  services_service_4_title: { id: "homepage.services.service-4.title", defaultMessage: "Rénovation" },
  services_service_4_text: { id: "homepage.services.service-4.text", defaultMessage: "Inter-Agence vous accompagne dans vos rénovations avec un suivi clair et fiable pour des travaux en toute sérénité." },
  news_title: { id: "homepage.section-news.title", defaultMessage: "Nos actualités dans l'immobilier à Nendaz" },
  news_text: { id: "homepage.section-news.text", defaultMessage: "Retrouvez ici les dernières nouvelles de l’agence, du marché immobilier local et de la vie de la station. Conseils, nouveautés et informations utiles à ne pas manquer." },
  news_btn: { id: "homepage.section-news.btn", defaultMessage: "Voir tous nos articles" },
  about_title: { id: "homepage.section-about.title", defaultMessage: "Apprenez en plus sur notre agence" },
  about_text: { id: "homepage.section-about.text", defaultMessage: "Depuis plus de 50 ans, Inter-Agence accompagne propriétaires et visiteurs avec une approche humaine et une vraie connaissance de Nendaz. Une histoire de confiance, transmise de génération en génération." },
  about_from: { id: "homepage.section-about.from", defaultMessage: "Depuis" },
  cta_title: { id: "homepage.section-cta.title", defaultMessage: "Vous souhaitez vendre votre bien ?" },
  cta_text: { id: "homepage.section-cta.text", defaultMessage: "Nous vous accompagnons avec sérieux et une connaissance précise du marché local." },
  cta_btn: { id: "homepage.section-cta.btn", defaultMessage: "Contactez-nous" },
  know_more_btn: { id: "homepage.know_more_btn", defaultMessage: "En savoir plus" },
});

const HomePage = () => {
  const intl = useIntl();
  const { transLib } = useLocaleFormatter();
  const { loading, newsContent } = useNewsContext();
  const [ propertiesDisplay, setPropertiesDisplay ] = useState<ArticleType>(ArticleType.RENTAL);

  return (
    <>
      <SeoMetas title={intl.formatMessage(transDefs.meta_title)}
                description={intl.formatMessage(transDefs.meta_description)}
                image={MainImage2500} />
      <Head>
        <link
          rel="preload"
          as="image"
          href={MainImage2500}
          imageSrcSet={heroImageSrcSet}
          imageSizes="100vw"
          type="image/jpeg"
          fetchPriority="high"
        />
      </Head>

      <Wrapper.Centered as="section"
                        className="hp hero">
        <div className="hero-content">
          <div className="hero-content__image">
            <picture className="rimg">
              <img
                src={MainImage2500}
                srcSet={heroImageSrcSet}
                sizes="100vw"
                width={2500}
                height={833}
                fetchPriority="high"
                alt={intl.formatMessage(transDefs.hero_subtitle)}
              />
            </picture>
            <SearchBar />
          </div>
          <div className="hero-content__title">
            <h1>INTER-AGENCE</h1>
            <p>{intl.formatMessage(transDefs.hero_subtitle)}</p>
          </div>
        </div>
      </Wrapper.Centered>

      {/*-- Our properties --*/}
      <Wrapper.TwoCol as="section"
                      className="hp propreties animate animate-text">
        <Wrapper.ColText>
            <h2>{intl.formatMessage(transDefs.properties_title)}</h2>
            <p>{intl.formatMessage(transDefs.properties_text)}</p>
            <div className="btns">
              {Object.values(ArticleType).map((type, index) => (
                <Button key={index}
                          type="button"
                          className={classNames("",{ active: type === propertiesDisplay })}
                          onClick={() => setPropertiesDisplay(type)}>{transLib.match("articleTypes", type)}
                </Button>
              ))}
            </div>
        </Wrapper.ColText>

        <Wrapper.ColImage>
            <ArticleCarousel type={propertiesDisplay} />
        </Wrapper.ColImage>

      </Wrapper.TwoCol>

      {/* Services */}
      <Wrapper.TwoCol as="section"
                className="hp services reverse bg-primary animate animate-text">
                  <Wrapper.ColText>
                    <h2>{intl.formatMessage(transDefs.services_title)}</h2>
                    <p>{intl.formatMessage(transDefs.services_text)}</p>
                  </Wrapper.ColText>

                  <Wrapper.ColImage>
                    <div>
                      <h3>{intl.formatMessage(transDefs.services_service_1_title)}</h3>
                      <p>{intl.formatMessage(transDefs.services_service_1_text)}</p>
                      <LocaleLink className="btn with-icon" to={"gerance"}>
                        {intl.formatMessage(transDefs.know_more_btn)}
                        <SpriteIcon hash="action.chevron.right"/>
                      </LocaleLink>
                    </div>
                    <div>
                      <h3>{intl.formatMessage(transDefs.services_service_2_title)}</h3>
                      <p>{intl.formatMessage(transDefs.services_service_2_text)}</p>
                      <LocaleLink className="btn with-icon" to={"estimation-immobiliere"}>
                        {intl.formatMessage(transDefs.know_more_btn)}
                        <SpriteIcon hash="action.chevron.right"/>
                      </LocaleLink>
                    </div>
                    <div>
                      <h3>{intl.formatMessage(transDefs.services_service_3_title)}</h3>
                      <p>{intl.formatMessage(transDefs.services_service_3_text)}</p>
                      <LocaleLink className="btn with-icon" to={"maintenance"}>
                        {intl.formatMessage(transDefs.know_more_btn)}
                        <SpriteIcon hash="action.chevron.right"/>
                      </LocaleLink>
                    </div>
                    <div>
                      <h3>{intl.formatMessage(transDefs.services_service_4_title)}</h3>
                      <p>{intl.formatMessage(transDefs.services_service_4_text)}</p>
                      <LocaleLink className="btn with-icon " to={"renovation"}>
                        {intl.formatMessage(transDefs.know_more_btn)}
                        <SpriteIcon hash="action.chevron.right"/>
                      </LocaleLink>
                    </div>
                  </Wrapper.ColImage>
      </Wrapper.TwoCol>

      {/* Actualités */}
      <Wrapper.TwoCol as="section"
                      className="hp news animate animate-text">
        <Wrapper.ColText>
          <h2>{intl.formatMessage(transDefs.news_title)}</h2>
          <p>{intl.formatMessage(transDefs.news_text)}</p>
          <LocaleLink className="btn primary" to={"actualites"}>{intl.formatMessage(transDefs.news_btn)}</LocaleLink>
        </Wrapper.ColText>
        <Wrapper.ColImage>
          {loading && Array.from({length: 4}).map((_, index) => <PostCard key={index} data={null} />)}
          {(!loading && newsContent) && newsContent.map((post: NewsEntry, index: number) => <PostCard key={index} data={post} />)}
        </Wrapper.ColImage>
      </Wrapper.TwoCol>

      {/* A Propos */}
      <section className="hp about wrap animate animate-text">
        <div>
          <picture>
            <source srcSet={AboutImageDesktop} media="(min-width: 768px)" />
            <source srcSet={AboutImageMobile} media="(max-width: 768px)" />
            <img src={AboutImageDesktop} alt={intl.formatMessage(transDefs.about_title)} loading="lazy" />
          </picture>
          <span>{intl.formatMessage(transDefs.about_from)} 1972</span>
        </div>

        <div className="glass-card">
          <div>
            <h2>{intl.formatMessage(transDefs.about_title)}</h2>
            <p>{intl.formatMessage(transDefs.about_text)}</p>
            <LocaleLink to={"a-propos"} className="btn primary">
              {intl.formatMessage(transDefs.know_more_btn)}
            </LocaleLink>
          </div>
        </div>
      </section>

      {/* CTA */}
      <Wrapper.CallToAction as="section"
                            className="animate animate-text">
        <Wrapper.CallToActionContent>
          <h2>{intl.formatMessage(transDefs.cta_title)}</h2>
          <p>{intl.formatMessage(transDefs.cta_text)}</p>
          <LocaleLink to={"contact"} className="btn primary">
            {intl.formatMessage(transDefs.cta_btn)}
          </LocaleLink>
        </Wrapper.CallToActionContent>
      </Wrapper.CallToAction>
    </>);

};

export default HomePage;
